import React from 'react';
import LegalLayout from '../../components/LegalLayout';
import { COMPANY, LAST_UPDATED } from './companyConfig';

const CancellationPolicy = () => (
  <LegalLayout
    title="Cancellation Policy"
    subtitle="Cancellation charges, the split between airline and ACF Tourism, and what to do."
    lastUpdated={LAST_UPDATED}
    canonicalPath="/cancellation-policy"
    description="ACF Tourism cancellation policy — airline penalties, platform handling fees, and step-by-step cancellation guidance."
  >
    <h2>1. Overview</h2>
    <p>
      Every flight cancellation involves <strong>two separate charges</strong>: the airline's
      cancellation penalty (governed by their fare rules) and {COMPANY.brand}'s handling
      fee. Both are deducted from the amount originally paid before the refund is returned
      to you.
    </p>

    <h2>2. Airline Cancellation Charges</h2>
    <p>
      The airline's cancellation charge depends on:
    </p>
    <ul>
      <li>Fare class / bucket of the ticket (e.g. Saver vs Flexi vs Business).</li>
      <li>Time remaining to departure (often higher penalties within 4–24 hours).</li>
      <li>Route (domestic vs international).</li>
      <li>Whether the ticket is marked <em>Non-Refundable</em> at booking time.</li>
    </ul>
    <p>
      The airline charge is <strong>passed through as-is</strong>. {COMPANY.brand} does not
      add a margin to this.
    </p>

    <h2>3. {COMPANY.brand} Cancellation Fee</h2>
    <table>
      <thead>
        <tr><th>Booking type</th><th>Cancellation fee</th></tr>
      </thead>
      <tbody>
        <tr><td>Domestic flight (one-way or round-trip)</td><td>INR 350 per passenger</td></tr>
        <tr><td>International flight</td><td>INR 1,000 per passenger</td></tr>
        <tr><td>No-show (cancellation after departure)</td><td>Non-refundable</td></tr>
      </tbody>
    </table>
    <p className="text-xs text-gray-500">
      The admin team may waive our fee at their discretion where a booking could not proceed
      due to a clear system issue on our side. GST at 18% applies on the retained fee for
      invoices issued to GSTIN-holding agents.
    </p>

    <h2>4. Reschedule / Date-Change Fee</h2>
    <p>
      Reschedule involves (a) the airline's reissue fee + fare difference and (b) our
      handling fee of <strong>INR 500 per passenger</strong> per change request.
    </p>
    <p>
      For B2B agents with GSTIN, a <strong>Supplementary Tax Invoice</strong> is issued for
      our handling fee at the time of reschedule — referencing the original tax invoice.
    </p>

    <h2>5. Name Correction</h2>
    <p>
      Minor name corrections (typos of up to 3 characters) are processed at airline cost +
      INR 500 per passenger handling fee, subject to airline acceptance. Full name changes
      are treated as new bookings by most airlines and are non-refundable.
    </p>

    <h2>6. SSR Changes (Seat / Baggage / Meal)</h2>
    <p>
      Post-booking ancillary purchases are charged at airline cost with no additional
      markup from {COMPANY.brand} (pass-through). Ancillary refunds follow airline rules
      and may not be refundable once utilised.
    </p>

    <h2>7. How to Cancel</h2>
    <ol>
      <li>Log in and open <strong>My Bookings</strong>.</li>
      <li>Select the booking and click <em>“Need Help”</em> → <em>Cancellation / Refund</em>.</li>
      <li>Describe the reason; our support team will confirm exact refund within 24 hours.</li>
      <li>
        Once approved, we process the refund to your original payment source per our{' '}
        <a href={COMPANY.urls.refund}>Refund Policy</a> timelines.
      </li>
    </ol>

    <h2>8. Force Majeure</h2>
    <p>
      If the airline cancels or significantly reschedules your flight due to weather, ATC,
      strikes, or other force-majeure events, cancellation charges are governed by the
      airline's compensation policy. In such cases our handling fee is typically waived —
      please escalate the specific case via a support ticket.
    </p>

    <h2>9. Contact</h2>
    <p>
      Cancellation-related queries:{' '}
      <a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a> ·{' '}
      {COMPANY.phone} ({COMPANY.phoneHours}).
    </p>
  </LegalLayout>
);

export default CancellationPolicy;
